import {
  childAgeGenderUnionConditionCodes,
  DEFAULT_AGE_QUOTA_CRITERIA,
  QUALIFICATION_CODE,
  Quota,
  SurveyQualification,
  SurveySelectableQualification,
  toConditionNamePairs,
} from '@ps/survey-utils';

import { FeasibilityCriteriaMapperBase } from '../../../utils/feasibility-criteria-mapper.base';
import { FeasibilityDefaultQuotaCriteriaDto } from '../dtos/feasibility.dto';

export class FeasibilityDefaultQuotaCriteriaPayloadMapper extends FeasibilityCriteriaMapperBase<FeasibilityDefaultQuotaCriteriaDto> {
  constructor(
    qualifications: SurveyQualification[],
    private readonly _quotas: readonly Quota[] = [],
  ) {
    super(qualifications);
  }

  protected override toCriterion(
    qualification: SurveyQualification,
  ): FeasibilityDefaultQuotaCriteriaDto {
    if (qualification.isRangeType) {
      return {
        qualification_code: qualification.qualificationCode,
        qualification_name: qualification.qualificationName,
        range_sets: qualification.rangeSets,
      };
    }

    if (
      qualification.qualificationCode === QUALIFICATION_CODE.CHILDREN_LEGACY
    ) {
      return this._childAgeGenderCriterion(qualification);
    }

    // ZIPCODE codes stay: quota rows need them. The names are never sent for a ZIPCODE, so
    // skip building tens of thousands of pairs. The `defaultQuota` payload empties the
    // codes too (withoutZipcodeConditions).
    const isZipcode =
      qualification.qualificationCode === QUALIFICATION_CODE.ZIPCODE;

    return {
      qualification_code: qualification.qualificationCode,
      qualification_name: qualification.qualificationName,
      condition_codes: qualification.conditionCodes,
      // No re-filter: `data` already holds exactly the selected conditions.
      condition_names: isZipcode
        ? []
        : toConditionNamePairs(qualification.data),
    };
  }

  protected override defaultAgeCriterion(): FeasibilityDefaultQuotaCriteriaDto {
    return {
      qualification_code: QUALIFICATION_CODE.AGE,
      qualification_name: DEFAULT_AGE_QUOTA_CRITERIA.NAME,
      range_sets: [
        {
          from: DEFAULT_AGE_QUOTA_CRITERIA.MIN,
          to: DEFAULT_AGE_QUOTA_CRITERIA.MAX,
          units: DEFAULT_AGE_QUOTA_CRITERIA.UNITS,
        },
      ],
    };
  }

  // Feasibility must see the real 232 codes here too, same as the survey-save payload's
  // qualification entry — the 218 flag's own ['111']/['112'] carries none of the age/gender
  // granularity the backend actually persists against. Built from the union of what the buyer's
  // actual Children quota rows target (see childAgeGenderUnionConditionCodes), not a synthesised
  // expansion of every possible age/gender combination — this criterion is what gets overlaid
  // onto every quota row that doesn't itself target Children, so an over-broad value here leaked
  // into every one of those rows' payloads, not just this standalone default entry.
  // condition_names is intentionally empty: CAG codes come from purespectrum-lib, which has no
  // display text, and CAG mapping must not depend on the catalog for names.
  private _childAgeGenderCriterion(
    qualification: SurveySelectableQualification,
  ): FeasibilityDefaultQuotaCriteriaDto {
    return {
      qualification_code: QUALIFICATION_CODE.CHILD_AGE_GENDER,
      qualification_name: qualification.qualificationName,
      condition_codes: childAgeGenderUnionConditionCodes(this._quotas),
      condition_names: [],
    };
  }
}
